
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { useAbility } from '@casl/vue';
  import { GroupObject } from '@/store/modules/members/GroupsModule';

  export default defineComponent({
    name: 'groups-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const selectedPermissions = ref([]);
      const selectedGroup = ref<number>(0);
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('groupsUpdating'), [
          translate('members'),
        ]);
      });

      const { data: group } = await store.dispatch(
        Actions.GET_GROUP_ID,
        route.params.id
      );

      const groupsData = ref<GroupObject>(group.data);

      await store.dispatch(Actions.GET_ALL_PERMISSIONS, { limit: 50 });
      const permissions = computed(() => store.getters.allpermissionsList);

      interface Option {
        key: number;
        label: string;
        disabled: boolean;
      }

      const generateData = () => {
        const data: Option[] = [];
        const permissionsList = permissions.value;
        if (Array.isArray(permissionsList)) {
          for (let i = 0; i < permissionsList.length; i++) {
            const permission = permissionsList[i];
            data.push({
              key: permission.toString(),
              label: permission.toString(),
              disabled: Boolean(permission.disabled),
            });
          }
        }
        return data;
      };

      const data = ref<Option[]>(generateData());
      const value = ref(
        groupsData.value.permissions.map((permission) => permission.title)
      );

      const goBack = () => {
        router.back();
      };

      const roleSchema = Yup.object().shape({
        title: Yup.string()
          .required(() => translate('GROUP_NAME_IS_REQUIRED_FIELD'))
          .label('Name'),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        values.permissions = value.value;

        await store.dispatch(Actions.UPDATE_GROUP, {
          data: values,
          id: route.params?.id,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_GROUP'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'groups-listing' });
          });
        }
      };

      await store.dispatch(Actions.GET_ROLES_GROUPS);

      const updatePermissionsList = (list) => {
        selectedPermissions.value = list;
      };

      return {
        groups: computed(() => store.getters.rolesGroupsList),
        updatePermissionsList,
        onSubmitCreate,
        permissions,
        data,
        groupsData,
        value,
        submitButton,
        translate,
        roleSchema,
        goBack,
        selectedGroup,
        can,
      };
    },
  });
